// externals
import type { Reducer } from 'redux';
import { LOCATION_CHANGE } from 'connected-react-router';

// libraries
import type { DealHistory, DealHistoryState } from '@makemydeal/dr-dash-types';

// actions
import type {
    DealHistoryActions,
    DealHistoryCheckedAction,
    DealHistorySelectedUpdate,
    DealHistoryUnCheckedAction,
    FetchDealHistorySuccessAction,
    ScenariosFetchSuccessAction
} from '../actionTypes/dealHistoryActionTypes';
import { DEAL_HISTORY_CHECKED, DEAL_HISTORY_UNCHECKED } from '../actionTypes/dealHistoryActionTypes';

// constants
import {
    FETCH_DEAL_HISTORY_FAILURE,
    FETCH_DEAL_HISTORY_REQUEST,
    FETCH_DEAL_HISTORY_SUCCESS,
    DEAL_HISTORY_SELECTED_UPDATE,
    SET_DEAL_HISTORY_SCENARIO_DRAWER_DEAL,
    DEAL_HISTORY_OPEN_DRAWER,
    DEAL_HISTORY_CLOSE_DRAWER,
    SET_MAKE_CURRENT_PENDING,
    CLEAR_MAKE_CURRENT_SUCCESS
} from '../actionTypes/dealHistoryActionTypes';
import {
    FETCH_DEALER_REVIEW_BAGGAGE_REQUEST,
    FETCH_DEALER_REVIEW_BAGGAGE_SUCCESS,
    FETCH_DEALER_REVIEW_BAGGAGE_FAILURE
} from '../actionTypes/offerActionTypes';
import { NavigationActions } from '@makemydeal/dr-activities-common';
import { BootstrapLoadingState } from '@makemydeal/dr-shared-types';

// types
import type { OfferResponse } from '@makemydeal/dr-dash-bff-types';
import { SCENARIOS_FETCH_SUCCESS, SCENARIOS_FETCH_FAILURE } from '../actionTypes/multiScenarioActionTypes';
import { bootstrapActionTypes, type FeatureTogglesAndUrlOnly } from '@makemydeal/dr-shared-store';
import { MAKE_CURRENT, MAKE_CURRENT_COMPLETED } from '../actionTypes/offerActionTypes';

// utils
import { mapOfferResponseToOffer } from '@makemydeal/dr-offer-redux';
import { AccessoriesAdapter } from '../utils/manualAccessoriesAdapter';
import { buildTradeInsStateFromTrades, initialState as tradeInsInitialState } from './tradeInsReducer';
import { buildTradeInsScopedStateFromTrades, initialState as tradeInsScopedInitialState } from './tradeInsScopedReducer';

export const dealHistoryInitialState: DealHistoryState = {
    offers: [],
    selectedDeals: [],
    drawerOpen: false
};

const getDealScopedNetTradeToggle = (featureToggles?: OfferResponse['featureToggles']): boolean =>
    (featureToggles as FeatureTogglesAndUrlOnly | undefined)?.DE769035_Enable_Deal_Scoped_Net_Trade ?? false;

const buildDealTradeInsState = (
    offerResponse: OfferResponse,
    isDealScopedNetTradeEnabled: boolean
): Partial<Pick<DealHistory, 'tradeIns' | 'tradeInsScoped'>> => {
    if (!isDealScopedNetTradeEnabled) {
        return {};
    }

    const singleTrade = offerResponse.trade?.trade;
    const trades = offerResponse.trades?.tradeIns?.length
        ? offerResponse.trades
        : singleTrade && Object.keys(singleTrade).length > 0
        ? { tradeIns: [singleTrade] }
        : undefined;

    return {
        tradeIns: buildTradeInsStateFromTrades(tradeInsInitialState, trades, true),
        tradeInsScoped: buildTradeInsScopedStateFromTrades(tradeInsScopedInitialState, trades)
    };
};

export type DealHistoryReducer = Reducer<DealHistoryState>;

export const reducer: DealHistoryReducer = (state: DealHistoryState = dealHistoryInitialState, action: DealHistoryActions) => {
    switch (action.type) {
        case FETCH_DEAL_HISTORY_FAILURE: {
            const newState: DealHistoryState = {
                ...state,
                isLoading: false
            };
            return newState;
        }
        case FETCH_DEAL_HISTORY_REQUEST: {
            const newState: DealHistoryState = {
                ...state,
                isLoading: true
            };
            return newState;
        }
        case FETCH_DEALER_REVIEW_BAGGAGE_REQUEST: {
            const newState: DealHistoryState = {
                ...state,
                pendingReviewDetecting: true
            };
            return newState;
        }
        case FETCH_DEALER_REVIEW_BAGGAGE_SUCCESS:
        case FETCH_DEALER_REVIEW_BAGGAGE_FAILURE: {
            const newState: DealHistoryState = {
                ...state,
                pendingReviewDetecting: false
            };
            return newState;
        }
        case FETCH_DEAL_HISTORY_SUCCESS: {
            const actionTyped = action as FetchDealHistorySuccessAction;
            const payload = actionTyped.payload || [];

            if (Array.isArray(payload)) {
                const offers: DealHistory[] = [];
                payload.forEach((offerResponse: OfferResponse) => {
                    /* Adding a check to filter out incomplete deals with missing payment data
                     * This can moved back to Backend API if needed
                     */
                    if (!offerResponse.offer.incompleteDealDetails?.missingPaymentData) {
                        const {
                            vehicle,
                            dealer,
                            dealXgDetails,
                            trade,
                            testDrive,
                            featureToggles,
                            dealVehicleProtection,
                            accessories: dealAccessories
                        } = offerResponse;
                        const accessories = AccessoriesAdapter.accessoriesToSourceFormat(dealAccessories || []);
                        const { products, surchargesOverrides: surcharges } = dealVehicleProtection || {};
                        const offer = mapOfferResponseToOffer(offerResponse);
                        offer.messageToDealer = offerResponse.offer.messageToDealer;
                        offer.employees = offerResponse.offer.employees || [];
                        offers.push({
                            accessories,
                            dealXgDetails,
                            sharedBootstrap: { loadingState: BootstrapLoadingState.LoadedSuccessfully },
                            offer,
                            vehicleProtectionV2: { products, surcharges },
                            vehicle,
                            dealer,
                            tradeIn: trade?.trade || {},
                            ...buildDealTradeInsState(offerResponse, getDealScopedNetTradeToggle(featureToggles)),
                            testDrive,
                            featureToggles
                        } as DealHistory);
                    }
                });

                const newState: DealHistoryState = {
                    ...state,
                    isLoading: false,
                    offers
                };
                return newState;
            }
            return state;
        }
        case DEAL_HISTORY_CHECKED: {
            const dealCheckedAction = action as DealHistoryCheckedAction;

            return dealCheckedAction.payload
                ? {
                      ...state,
                      selectedDeals: [...(state.selectedDeals as unknown as DealHistory[]), dealCheckedAction.payload]
                  }
                : state;
        }
        case DEAL_HISTORY_UNCHECKED: {
            const offerIdToBeRemoved = (action as DealHistoryUnCheckedAction).payload;

            return {
                ...state,
                selectedDeals: [
                    ...(state.selectedDeals as DealHistory[]).filter(
                        (selectedDeal) => selectedDeal.offer.offerId !== offerIdToBeRemoved
                    )
                ]
            };
        }

        case LOCATION_CHANGE: {
            return action.payload?.location?.pathname === '/dealHistory'
                ? {
                      ...state,
                      selectedDeals: []
                  }
                : state;
        }

        case DEAL_HISTORY_SELECTED_UPDATE: {
            const offerIdToBeSelected = (action as DealHistorySelectedUpdate).payload;
            const selectedDeals = (state.selectedDeals as DealHistory[]).map((selectedDeal) => {
                selectedDeal.isSelected = selectedDeal.offer.offerId === offerIdToBeSelected;
                return selectedDeal;
            });

            return {
                ...state,
                selectedDeals
            };
        }

        case NavigationActions.NAVIGATE_TO: {
            return state.selectedDeals?.length
                ? {
                      ...state,
                      selectedDeals: []
                  }
                : state;
        }

        case SCENARIOS_FETCH_SUCCESS: {
            const actionTyped = action as ScenariosFetchSuccessAction;
            const payload = actionTyped.payload;
            const dealXgVersion = actionTyped.meta?.dealXgVersion;

            if (!payload || !payload.scenarios || !dealXgVersion) {
                return state;
            }

            const offers: DealHistory[] = payload.scenarios.map((offerResponse: OfferResponse) => {
                const {
                    vehicle,
                    dealer,
                    dealXgDetails,
                    trade,
                    testDrive,
                    featureToggles,
                    dealVehicleProtection,
                    accessories: dealAccessories
                } = offerResponse;
                const accessories = AccessoriesAdapter.accessoriesToSourceFormat(dealAccessories || []);
                const { products, surchargesOverrides: surcharges } = dealVehicleProtection || {};
                const offer = mapOfferResponseToOffer(offerResponse);
                offer.messageToDealer = offerResponse.offer.messageToDealer;
                offer.employees = offerResponse.offer.employees || [];

                return {
                    accessories,
                    dealXgDetails,
                    sharedBootstrap: { loadingState: BootstrapLoadingState.LoadedSuccessfully },
                    offer,
                    vehicleProtectionV2: { products, surcharges },
                    vehicle,
                    dealer,
                    tradeIn: trade?.trade || {},
                    ...buildDealTradeInsState(offerResponse, getDealScopedNetTradeToggle(featureToggles)),
                    testDrive,
                    featureToggles
                } as DealHistory;
            });

            const scenarios = { ...state.scenarios, [dealXgVersion]: offers };

            const newState: DealHistoryState = {
                ...state,
                scenarios
            };
            return newState;
        }

        case SET_DEAL_HISTORY_SCENARIO_DRAWER_DEAL: {
            return { ...state, dealHistoryScenarioDrawerDeal: action.payload };
        }

        case DEAL_HISTORY_OPEN_DRAWER: {
            return { ...state, drawerOpen: true };
        }

        case DEAL_HISTORY_CLOSE_DRAWER: {
            return { ...state, drawerOpen: false, selectedDeals: [] };
        }

        case MAKE_CURRENT:
        case MAKE_CURRENT_COMPLETED: {
            const newState: DealHistoryState = { ...state, drawerOpen: false, dealHistoryScenarioDrawerDeal: undefined };
            if (action.type === MAKE_CURRENT_COMPLETED && state.makeCurrentPending) {
                newState.makeCurrentPending = false;
                newState.makeCurrentSuccess = true;
            }
            return newState;
        }

        case bootstrapActionTypes.BOOTSTRAP_FAILURE:
        case SCENARIOS_FETCH_FAILURE: {
            return state.makeCurrentPending ? { ...state, makeCurrentPending: false } : state;
        }

        case SET_MAKE_CURRENT_PENDING: {
            return { ...state, makeCurrentPending: true };
        }

        case CLEAR_MAKE_CURRENT_SUCCESS: {
            return { ...state, makeCurrentSuccess: false };
        }

        default: {
            return state;
        }
    }
};
